/* The identity-service session protocol, in-app (replaces the unmaintained
 * @ai21/platform-auth-sdk — see session.tsx for the rationale):
 *
 *   POST /identity/sign-in   { firebase_token, product, invitation_id? }
 *     Exchanges a Firebase ID token for the identity session cookie and
 *     JIT-provisions the org/user. `product: "tokenwise"` selects the
 *     Tokenwise SparkPost branding; on first sign-in of a new unverified
 *     address the backend sends the verification email. NOTE: sign-in does
 *     NOT reject unverified users — it always sets the cookie. The verified
 *     gate is the `emailVerified` field on GET /identity/user (see session.tsx
 *     and how studio gates: ProtectedRoute reads isUserVerified), so a non-2xx
 *     here is a genuine failure. `invitation_id` joins the invited org
 *     directly (threaded through email sign-up only — see session.tsx).
 *   POST /identity/resend-email-verification   { product }
 *     (Re)sends the verification email; cookie-authenticated. Server rate
 *     limit is 1/min.
 *   GET  /identity/user
 *     Returns the session's user incl. `emailVerified` (cookie auth).
 *     Invitations are accepted via the dedicated /invite/:id page.
 *
 * All ride the session cookie (credentials: "include"), like logout.ts's
 * /identity/end-session. */

import { IDENTITY_BASE_URL } from "./config";
import { PRODUCT } from "../product";

/** The slice of /identity/user this app consumes. */
export interface AuthUser {
  userId: string;
  userName: string;
  userEmail: string;
  userRole?: "ADMIN" | "MEMBER";
  organizationId?: string;
  organizationName?: string;
  userPictureUrl?: string;
  /** Stigg customer id, used to harden the client-side Stigg SDK (OPSS-55).
   *  TODO(OPSS-55): identity must include this on GET /identity/user; until it
   *  does, Stigg hardening is skipped gracefully. */
  stiggCustomerId?: string;
  /** Whether the backend considers this user's email verified. Social
   *  providers are always verified; an email/password user is verified only
   *  after following the verification link. Undefined ⇒ treat as verified
   *  (matches studio's createUserFromIdentityUser default). This field — not a
   *  sign-in rejection — is the verified gate (see session.tsx). */
  emailVerified?: boolean;
}

export type SignInErrorCode = "sign_in_failed" | "email_not_allowed";

export class SignInError extends Error {
  readonly code: SignInErrorCode;

  constructor(code: SignInErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

/** Exchange a Firebase ID token for the identity session cookie.
 *
 *  `invitationId` is normally omitted: a signed-in viewer with a pending
 *  invite is forwarded to the dedicated /invite/:id accept page. It is passed
 *  only for email SIGN-UP from an invite deeplink, so the brand-new account
 *  joins the invited org directly instead of JIT-creating a throwaway org. */
export async function identitySignIn(
  firebaseToken: string,
  invitationId?: string,
): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE_URL}/identity/sign-in`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      firebase_token: firebaseToken,
      // Selects this product's email branding (e.g. the verification email's
      // SparkPost template). Exactly "tokenwise" — unknown values 422.
      product: PRODUCT,
      ...(invitationId ? { invitation_id: invitationId } : {}),
    }),
  });
  if (res.ok) return;

  // Off-list AI21 Gateway emails come back 403 { detail: { code: "EMAIL_NOT_ALLOWLISTED" } } — surface a distinct code for the coming-soon redirect.
  if (
    res.status === 403 &&
    (await extractErrorCode(res)) === "EMAIL_NOT_ALLOWLISTED"
  ) {
    throw new SignInError(
      "email_not_allowed",
      "This email isn't allowed to access the gateway yet.",
    );
  }

  // The verified gate is the `emailVerified` field on GET /identity/user (see
  // session.tsx), NOT a sign-in rejection — /sign-in sets the cookie and
  // returns success even for an unverified email. So every other non-2xx here
  // is a genuine sign-in failure.
  throw new SignInError(
    "sign_in_failed",
    `Identity sign-in failed (HTTP ${res.status}).`,
  );
}

/** The `code` from an identity error body `{ detail: { code, message } }`, or null. */
async function extractErrorCode(res: Response): Promise<string | null> {
  const body = (await res.json().catch(() => null)) as {
    detail?: { code?: unknown } | string;
  } | null;
  const detail = body?.detail;
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    return detail.code;
  }

  return null;
}

/** The session's user, or null when there is no (valid) session cookie.
 *  Only 401/403 mean "not signed in"; anything else (identity down, 5xx)
 *  throws so callers can log it instead of silently treating a healthy
 *  session as anonymous. */
export async function fetchSessionUser(): Promise<AuthUser | null> {
  const res = await fetch(`${IDENTITY_BASE_URL}/identity/user`, {
    credentials: "include",
  });
  if (res.status === 401 || res.status === 403) return null;

  if (!res.ok) {
    throw new Error(`Fetching the session user failed (HTTP ${res.status}).`);
  }

  const data = (await res.json().catch(() => null)) as AuthUser | null;
  return data?.userId ? data : null;
}

/** Ask the identity service to (re)send the tokenwise-branded verification
 *  email. Cookie-authenticated — the session cookie is set by /sign-in even
 *  before the email is verified, so this works from the check-email screen.
 *  `product` selects the tokenwise SparkPost template. The server rate-limits
 *  this to 1/min (HTTP 429), which the UI surfaces via a cooldown. */
export async function resendEmailVerification(): Promise<void> {
  const res = await fetch(
    `${IDENTITY_BASE_URL}/identity/resend-email-verification`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product: PRODUCT }),
    },
  );
  if (!res.ok) {
    throw new Error(`Resend verification failed (HTTP ${res.status}).`);
  }
}
